import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../core/auth.service';
import { validateValue } from '../../shared/validation';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [FormsModule, NgIf, RouterLink, MatIconModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  token = '';
  email = '';
  password = '';
  confirmPassword = '';
  error = '';
  loading = true;
  submitting = false;
  validToken = false;
  showPassword = false;
  showConfirmPassword = false;

  constructor(private auth: AuthService, private route: ActivatedRoute, private router: Router) {}

  /**
   * Reads the reset token from the route and validates it with the backend.
   * @returns void.
   */
  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (this.route.snapshot.queryParamMap.get('invalid')) {
      this.error = 'Reset link is invalid, expired, or already used';
      this.loading = false;
      return;
    }

    if (!this.token) {
      this.error = 'Reset link is invalid or expired';
      this.loading = false;
      return;
    }

    this.auth.validateResetToken(this.token).subscribe({
      next: (result) => {
        this.validToken = result.valid;
        this.email = result.email;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || 'Reset link is invalid or expired';
        this.loading = false;
      }
    });
  }

  /**
   * Validates password complexity and submits the new password for the reset token.
   * @returns void.
   */
  submit() {
    this.error = '';
    const validationMessage = this.validatePassword();
    if (validationMessage) {
      this.error = validationMessage;
      return;
    }

    this.submitting = true;
    this.auth.resetPassword(this.token, this.password, this.confirmPassword).subscribe({
      next: (result) => {
        this.router.navigate(['/login'], {
          queryParams: { message: result.message || 'Password reset successful. Please sign in.' }
        });
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to reset password';
        this.submitting = false;
      }
    });
  }

  /**
   * Toggles password visibility for the selected password field.
   * @param field Password field to update.
   * @param value Visibility state.
   * @returns void.
   */
  reveal(field: 'password' | 'confirm', value: boolean) {
    if (field === 'password') this.showPassword = value;
    if (field === 'confirm') this.showConfirmPassword = value;
  }

  /**
   * Applies the same password complexity rules used by the backend.
   * @returns Empty string when valid; otherwise a user-facing validation message.
   */
  private validatePassword() {
    const passwordMessage = validateValue('Password', this.password, { required: true, kind: 'password', minLength: 8, maxLength: 64 });
    if (passwordMessage) return passwordMessage;
    if (!this.confirmPassword) return 'Confirm password is required';
    if (this.password !== this.confirmPassword) return 'Confirm password must match new password';
    return '';
  }
}
